import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import {
  Button,
  ButtonGroup,
  OverlayTrigger,
  Popover,
  ToggleButton
} from 'react-bootstrap';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FilterSpecifications from './FilterSpecifications';
import Divider from '@/components/common/Divider';
import { checkModeToText, modeToText } from '@/utils/toTextFunctions';
import { timestampToJalali } from '@/utils/time';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import type {
  AdvancedFiltersProps,
  FilterMode,
  FilterOperator,
  NumericKeyCounter,
  SelectItems
} from './filterTypes';

const getSelectLabel = (
  selectItems: SelectItems | undefined,
  field: string | undefined,
  value: FilterMode['value']
) =>
  selectItems?.[field ?? '']?.find(selectItem => selectItem.value == value)
    ?.label ?? '';

const AdvancedFilters2 = ({
  columnKey,
  columnValue,
  advancedFilters,
  setAdvancedFilters,
  setFilteredData,
  selectItems,
  placement
}: AdvancedFiltersProps) => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const [showModal, setShowModal] = useState(false);
  const keyCounter = useRef<NumericKeyCounter>(1);
  const [operator, setOperator] = useState<FilterOperator>(
    advancedFilters[columnKey].operator || 'and'
  );
  const [filterModes, setFilterModes] = useState<FilterMode[]>([]);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (advancedFilters[columnKey].filterModes.length == 0) {
      buttonRef.current?.click();
      setShowModal(true);
    }
  }, [buttonRef]);

  useEffect(() => {
    if (advancedFilters[columnKey].filterModes.length !== 0) {
      setFilterModes(advancedFilters[columnKey].filterModes);
    }
  }, []);

  const handleChangeOperator = (value: FilterOperator) => {
    setOperator(value);
    setAdvancedFilters(prev => {
      return {
        ...prev,
        [columnKey]: {
          ...prev[columnKey],
          operator: value
        }
      };
    });
  };

  const handleClearFilters = () => {
    const filters = { ...advancedFilters };
    delete filters[columnKey];
    setAdvancedFilters(filters);
    !_.isEmpty(filters) ? setFilteredData(false) : setFilteredData(true); // برای نمایش دوباره دکمه اعماال فیلتر
  };

  const handleInsertFilters = () => {
    buttonRef.current?.click();
    setShowModal(false);
    setAdvancedFilters(prev => {
      return {
        ...prev,
        [columnKey]: {
          ...prev[columnKey],
          filterModes
        }
      };
    });
    setFilteredData(false);
  };

  return (
    <>
      {isMdUp ? (
        /** Desktop View - Use OverlayTrigger with Popover */
        <OverlayTrigger
          trigger="click"
          rootClose
          onExited={() => {
            if (advancedFilters[columnKey].filterModes.length === 0) {
              const filters = { ...advancedFilters };
              delete filters[columnKey];
              setAdvancedFilters(filters);
            }
          }}
          placement={placement}
          overlay={
            <Popover style={{ maxWidth: '100%', minWidth: 509 }}>
              <Popover.Header as="h6">
                {' '}
                فیلتر بر اساس " {columnValue.title} "
              </Popover.Header>
              <Popover.Body className="p-0">
                <div className="p-2">
                  <div className="px-3 pt-3">
                    <ButtonGroup>
                      <ToggleButton
                        size="sm"
                        id={`radio-checkMode-and`}
                        type="radio"
                        variant={'outline-secondary'}
                        name="checkModeAnd"
                        value={'and'}
                        checked={operator === 'and'}
                        onChange={e =>
                          handleChangeOperator(
                            e.currentTarget.value as FilterOperator
                          )
                        }
                      >
                        همه شرط (and)
                      </ToggleButton>
                      <ToggleButton
                        size="sm"
                        id={`radio-checkMode-or`}
                        type="radio"
                        variant={'outline-secondary'}
                        name="checkModeOr"
                        value={'or'}
                        checked={operator === 'or'}
                        onChange={e =>
                          handleChangeOperator(
                            e.currentTarget.value as FilterOperator
                          )
                        }
                      >
                        هر شرط (or)
                      </ToggleButton>
                    </ButtonGroup>
                  </div>
                  <div className="px-3">
                    <FilterSpecifications
                      columnValue={columnValue}
                      filterModes={filterModes}
                      setFilterModes={setFilterModes}
                      selectItems={selectItems}
                      ref={keyCounter}
                    />
                  </div>
                </div>
                <Divider className="my-0 p-0" />
                <div className="d-flex align-items-center gap-2 px-2 pb-2">
                  <ChildrensTooltip
                    placement="top"
                    html={filterModes.length > 0 ? '' : <>فیلتری اضافه نکردی</>}
                  >
                    <Button
                      size="sm"
                      variant="falcon-primary"
                      onClick={() => {
                        filterModes.length > 0 && handleInsertFilters();
                      }}
                    >
                      <span>ثبت</span>
                    </Button>
                  </ChildrensTooltip>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleClearFilters}
                  >
                    لغو
                  </Button>
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <Button
            size={undefined}
            variant={`${
              advancedFilters[columnKey].filterModes.length == 0
                ? 'falcon-default'
                : 'btn-outline-default'
            }`}
            className={`${
              advancedFilters[columnKey].filterModes.length != 0 && 'p-0'
            } m-0 fs--1 cursor-pointer d-flex align-items-center`}
            ref={buttonRef}
          >
            {advancedFilters[columnKey].filterModes.length == 0 ? (
              <>
                {/* <FontAwesomeIcon /> */}
                <span>{columnValue.title}</span>
              </>
            ) : (
              <Button
                variant="falcon-default"
                size={undefined}
                className="m-0 fs--1 cursor-pointer d-flex align-items-center"
              >
                <span>{columnValue.title}: </span>
                {advancedFilters[columnKey].filterModes.map((item, index) => (
                  <span key={index}>
                    <span className="text-wrap fw-normal">
                      <span>{modeToText(item.mode)}</span>
                      <span className="fw-black">
                        {columnValue.type !== 'boolean' &&
                        columnValue.type != 'select' &&
                        columnValue.type != 'date' ? (
                          <span> {item.value} </span>
                        ) : columnValue.type === 'date' ? (
                          <span>{timestampToJalali(item.value)}</span>
                        ) : columnValue.type == 'boolean' ? (
                          item.value === 'true' ? (
                            <span>&nbsp;بله</span>
                          ) : (
                            <span>&nbsp;خیر</span>
                          )
                        ) : (
                          <span>
                            {' '}
                            {item.mode !== 'blank' && item.mode !== 'notblank'
                              ? getSelectLabel(
                                  selectItems,
                                  columnValue.field,
                                  item.value
                                )
                              : ''}
                          </span>
                        )}
                      </span>
                      {index !=
                        advancedFilters[columnKey].filterModes.length - 1 && (
                        <span className="mx-1">
                          {checkModeToText(advancedFilters[columnKey].operator)}
                        </span>
                      )}
                    </span>
                  </span>
                ))}
                <span className="vr opacity-25 ms-2" />
                <span
                  onClick={handleClearFilters}
                  style={{
                    marginRight: '6px',
                    marginLeft: '-8px'
                  }}
                  className="d-flex justify-content-center align-items-center"
                >
                  <FontAwesomeIcon
                    icon="times"
                    className=""
                    style={{
                      color: 'inherit'
                    }}
                  />
                </span>
              </Button>
            )}
          </Button>
        </OverlayTrigger>
      ) : (
        /** Mobile View - Use Modal */
        <>
          <Button
            onClick={() => {
              setShowModal(true);
            }}
            size={undefined}
            variant={`${
              advancedFilters[columnKey].filterModes.length == 0
                ? 'falcon-default'
                : 'btn-outline-default'
            }`}
            className={`${
              advancedFilters[columnKey].filterModes.length != 0 && 'p-0'
            } m-0 fs--1 cursor-pointer d-flex align-items-center text-nowrap`}
            ref={buttonRef}
          >
            {advancedFilters[columnKey].filterModes.length == 0 ? (
              <>
                {/* <FontAwesomeIcon /> */}
                <span className="text-nowrap">{columnValue.title}</span>
              </>
            ) : (
              <Button
                variant="falcon-default"
                size={undefined}
                className="m-0 text-nowrap fs--1 cursor-pointer d-flex align-items-center"
              >
                <span className="d-flex align-items-center flex-wrap text-nowrap">
                  <span>{columnValue.title}: </span>
                  {advancedFilters[columnKey].filterModes.map((item, index) => (
                    <span key={index}>
                      <span className="text-nowrap fw-normal">
                        <span className="text-nowrap">
                          {modeToText(item.mode)}
                        </span>
                        <span className="fw-black text-nowrap">
                          {columnValue.type !== 'boolean' &&
                          columnValue.type != 'select' &&
                          columnValue.type != 'date' ? (
                            <span className="text-nowrap"> {item.value} </span>
                          ) : columnValue.type === 'date' ? (
                            <span className="text-nowrap">
                              {timestampToJalali(item.value)}
                            </span>
                          ) : columnValue.type == 'boolean' ? (
                            item.value === 'true' ? (
                              <span>&nbsp;بله</span>
                            ) : (
                              <span>&nbsp;خیر</span>
                            )
                          ) : (
                            <span className="text-nowrap">
                              {' '}
                              {getSelectLabel(
                                selectItems,
                                columnValue.field,
                                item.value
                              )}
                            </span>
                          )}
                        </span>
                        {index !=
                          advancedFilters[columnKey].filterModes.length - 1 && (
                          <span className="mx-1 text-nowrap">
                            {checkModeToText(
                              advancedFilters[columnKey].operator
                            )}
                          </span>
                        )}
                      </span>
                    </span>
                  ))}
                </span>
                <span className="vr opacity-25 ms-2" />
                <span
                  onClick={handleClearFilters}
                  style={{
                    marginRight: '6px',
                    marginLeft: '-8px'
                  }}
                  className="d-flex justify-content-center align-items-center"
                >
                  <FontAwesomeIcon
                    icon="times"
                    className=""
                    style={{
                      color: 'inherit'
                    }}
                  />
                </span>
              </Button>
            )}
          </Button>

          <Modal
            show={showModal}
            onHide={() => {
              if (advancedFilters[columnKey].filterModes.length === 0) {
                const filters = { ...advancedFilters };
                delete filters[columnKey];
                setAdvancedFilters(filters);
              }
              setShowModal(false);
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title>فیلتر بر اساس "{columnValue.title}"</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="px-3 pt-3">
                <ButtonGroup>
                  <ToggleButton
                    size="sm"
                    id="radio-checkMode-and"
                    type="radio"
                    variant="outline-secondary"
                    name="checkModeAnd"
                    value="and"
                    checked={operator === 'and'}
                    onChange={e =>
                      handleChangeOperator(
                        e.currentTarget.value as FilterOperator
                      )
                    }
                  >
                    همه شرط (and)
                  </ToggleButton>
                  <ToggleButton
                    size="sm"
                    id="radio-checkMode-or"
                    type="radio"
                    variant="outline-secondary"
                    name="checkModeOr"
                    value="or"
                    checked={operator === 'or'}
                    onChange={e =>
                      handleChangeOperator(
                        e.currentTarget.value as FilterOperator
                      )
                    }
                  >
                    هر شرط (or)
                  </ToggleButton>
                </ButtonGroup>
              </div>
              <div className="px-3">
                <FilterSpecifications
                  columnValue={columnValue}
                  filterModes={filterModes}
                  setFilterModes={setFilterModes}
                  selectItems={selectItems}
                  ref={keyCounter}
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex align-items-center gap-2 px-2 pb-2">
                <ChildrensTooltip
                  placement="top"
                  html={filterModes.length > 0 ? '' : <>فیلتری اضافه نکردی</>}
                >
                  <Button
                    size="sm"
                    variant="falcon-primary"
                    onClick={() => {
                      filterModes.length > 0 && handleInsertFilters();
                    }}
                  >
                    <span>ثبت</span>
                  </Button>
                </ChildrensTooltip>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                >
                  لغو
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
};

export default AdvancedFilters2;
